/**
 * studioQuotation — the one definition of the sales catalogue and the Studio
 * Starter deposit/instalment maths.
 *
 * Extracted from IssueQuotationModal on 2026-09-11 when the Add Customer page
 * gained the same controls (BRD FR-5). Copying it would have created a third
 * home for R3,990 and the R500 floor — the EF holds STUDIO_TOTAL_CENTS and the
 * saga used to hold a bare `399000` literal, which is exactly the drift the
 * 2026-09-11 migration removed by reading service_sku.price_cents instead.
 *
 * Anything priced here is a display default only. The EF revalidates every
 * figure and is the authority; these values exist so an agent sees the right
 * number during a live call.
 */

// Currency formatting is NOT redefined here. `formatZARFromCents` is the one
// implementation (shared-component doctrine R3) — a local Intl.NumberFormat is
// how `formatZAR` came to mean rands in ~22 files and cents in ~5, a 100x error
// with no type error to catch it. Re-exported under the short name the existing
// call sites already use.
export { formatZARFromCents as zar } from '@/shared/utils/format';

export const STUDIO_TOTAL_CENTS = 399000;      // R3,990
export const STUDIO_DEPOSIT_FLOOR_CENTS = 50000; // R500 (locked 2026-07-16)

/** Catalogue offered to agents. Bundles are pseudo-SKUs — see BUNDLE_ITEMS. */
export const SALES_SKUS = [
  { code: 'studio-starter', label: 'Studio Starter', hint: 'R3,990 website' },
  { code: 'care-pro', label: 'SightHost Care', hint: 'R199/month' },
  { code: 'email-setup', label: 'Email Setup & Training', hint: 'R350 once-off' },
  { code: 'site-boost', label: 'Site Boost', hint: 'R499 once-off' },
  { code: 'gbp-setup', label: 'Local Visibility (Google Business Profile)', hint: 'R499 once-off' },
  // Unbundled from Studio Starter 2026-08-29 — both were free inclusions and
  // are now sold separately. Studio held at R3,990. Orders and quotations
  // issued before that date keep both at no charge (Terms §256 as published).
  { code: 'logo-design', label: 'Logo Design', hint: 'R300 once-off' },
  { code: 'company-profile', label: 'Company Profile', hint: 'R850 once-off (6–10 pages)' },
  { code: 'bundle:get-found-online', label: 'Get Found Online bundle', hint: 'R899 — Site Boost + GBP, save R99' },
] as const;

export type SkuCode = (typeof SALES_SKUS)[number]['code'];

/** One-off add-ons: everything except Studio, which has its own branch. */
export const ADDON_SKUS = SALES_SKUS.filter((s) => s.code !== 'studio-starter');

/** Bundle pseudo-SKUs map to the EF's items[] input. */
export const BUNDLE_ITEMS: Record<string, string[]> = {
  'bundle:get-found-online': ['site-boost', 'gbp-setup'],
};

/**
 * Deposit presets offered as bubbles (AC-9). `null` is the "Other" bubble,
 * which reveals a free-text amount. R500 is both the floor and the common
 * close, so it is the default.
 */
export const STUDIO_DEPOSIT_PRESETS_CENTS = [50000, 100000, 150000, 200000] as const;

export const STUDIO_PLAN_MONTHS = [3, 6, 12] as const;
export type StudioPlanMonths = (typeof STUDIO_PLAN_MONTHS)[number];

export function isDepositValid(depositCents: number): boolean {
  return (
    Number.isFinite(depositCents) &&
    depositCents >= STUDIO_DEPOSIT_FLOOR_CENTS &&
    depositCents < STUDIO_TOTAL_CENTS
  );
}

export interface InstallmentPreview {
  balanceCents: number;
  installmentCents: number;
  /** Sub-rand residual, disclosed to the customer as a discount (ST-6). */
  roundingCents: number;
}

/**
 * ST-6 floor variant. The instalment is floor(balance / months) and the
 * residual — at most (months − 1) cents — is a DISCOUNT, not a larger final
 * payment: PayFast Recurring collects a fixed amount per cycle, so a bigger
 * last instalment is impossible. Mirrors the saga exactly (integer division in
 * Postgres truncates), which is why both must stay floor.
 */
export function installmentPreview(
  depositCents: number,
  months: StudioPlanMonths,
  totalCents: number = STUDIO_TOTAL_CENTS
): InstallmentPreview | null {
  if (!isDepositValid(depositCents)) return null;
  const balanceCents = totalCents - depositCents;
  const installmentCents = Math.floor(balanceCents / months);
  return {
    balanceCents,
    installmentCents,
    roundingCents: balanceCents - installmentCents * months,
  };
}
